import { adminService } from "@/api/admin/admin.service";
import { TAdminCompany, TCompanyCountStats, TCompanyStatsOverview } from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TFilterDef,
} from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "@tanstack/react-router";
import { Eye, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

const PAGE_SIZE = 20;

function RoleBadge({ role }: { role: TAdminCompany["role"] }) {
  return (
    <Badge variant={role === "FUNDRAISER" ? "default" : "secondary"}>
      {role === "FUNDRAISER" ? "Fundraiser" : "Investor"}
    </Badge>
  );
}

function OnboardingBadge({ completed }: { completed: boolean }) {
  return (
    <Badge
      variant="outline"
      className={
        completed
          ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400"
          : "border-border bg-muted text-muted-foreground"
      }
    >
      {completed ? "Completed" : "Pending"}
    </Badge>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

export function CompaniesPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<TCompanyStatsOverview | null>(null);
  const [byType, setByType] = useState<TCompanyCountStats | null>(null);
  const [byStatus, setByStatus] = useState<TCompanyCountStats | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const loadStats = useCallback(async (signal: AbortSignal) => {
    const [overviewRes, byTypeRes, byStatusRes] = await Promise.all([
      adminService.getCompanyStatsOverview({ signal }),
      adminService.getCompanyStatsByType({ signal }),
      adminService.getCompanyStatsByStatus({ signal }),
    ]);
    if (signal.aborted) return;
    if (!overviewRes.error && overviewRes.data) setOverview(overviewRes.data.data);
    if (!byTypeRes.error && byTypeRes.data) setByType(byTypeRes.data.data);
    if (!byStatusRes.error && byStatusRes.data) setByStatus(byStatusRes.data.data);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadStats(ctrl.signal);
    return () => ctrl.abort();
  }, [loadStats]);

  const fetcher = async (query: TDataGridQuery) => {
    const response = await adminService.getCompanies(
      {
        limit: query.limit,
        offset: query.offset,
        ...(query.search ? { search: query.search } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
        ...(typeof query.filters.role === "string" && query.filters.role
          ? { role: query.filters.role }
          : {}),
        ...(typeof query.filters.onboarding_completed === "string" &&
        query.filters.onboarding_completed
          ? { onboarding_completed: query.filters.onboarding_completed === "true" }
          : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const columns: TColumnDef<TAdminCompany>[] = [
    {
      id: "name",
      header: "Company",
      sortable: true,
      sortKey: "name",
      cell: (c) => (
        <div>
          <div className="text-sm font-medium text-foreground">{c.name}</div>
          <div className="font-mono text-xs text-muted-foreground">{c.id}</div>
        </div>
      ),
    },
    {
      id: "role",
      header: "Type",
      sortable: true,
      sortKey: "role",
      cell: (c) => <RoleBadge role={c.role} />,
    },
    {
      id: "onboarding_completed",
      header: "Onboarding",
      sortable: true,
      sortKey: "onboarding_completed",
      cell: (c) => <OnboardingBadge completed={c.onboarding_completed} />,
    },
    {
      id: "summary_generation_status",
      header: "Summary",
      accessor: (c) => c.summary_generation_status ?? "-",
    },
    { id: "created_at", header: "Created", type: "date", sortable: true, sortKey: "created_at" },
  ];

  const filters: TFilterDef[] = [
    {
      id: "role",
      type: "select",
      label: "Type",
      options: [
        { label: "Fundraiser", value: "FUNDRAISER" },
        { label: "Investor", value: "INVESTOR" },
      ],
    },
    {
      id: "onboarding_completed",
      type: "select",
      label: "Onboarding status",
      options: [
        { label: "Completed", value: "true" },
        { label: "Pending", value: "false" },
      ],
    },
  ];

  const handleView = (companyId: string) => {
    void navigate({ to: "/app/admin/companies/$company_id", params: { company_id: companyId } });
  };

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Companies</h1>
            <p className="mt-1 text-sm text-muted-foreground">Browse and inspect all companies on the platform</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              abortRef.current?.abort();
              const ctrl = new AbortController();
              abortRef.current = ctrl;
              void loadStats(ctrl.signal);
              setReloadTick((t) => t + 1);
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label="Total Companies"
            value={overview?.total_companies ?? "-"}
          />
          <StatCard
            label="Total Sections"
            value={overview?.total_sections ?? "-"}
          />
          <StatCard
            label="Fundraisers"
            value={byType?.fundraiser ?? "-"}
          />
          <StatCard
            label="Investors"
            value={byType?.investor ?? "-"}
          />
        </div>
        {byStatus && Object.keys(byStatus).length > 0 && (
          <p className="text-xs text-muted-foreground">
            By status:{" "}
            {Object.entries(byStatus)
              .map(([key, value]) => `${key}: ${value}`)
              .join(" • ")}
          </p>
        )}

        <DataGrid<TAdminCompany>
          getRowId={(row) => row.id}
          persistKey="admin.companies"
          ariaLabel="Companies"
          data={{ mode: "server", fetcher, deps: [reloadTick] }}
          columns={columns}
          filters={filters}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search by company name",
          }}
          onRowClick={(company) => handleView(company.id)}
          rowActions={(company) => [
            {
              label: "View",
              icon: <Eye className="h-4 w-4" />,
              onClick: () => handleView(company.id),
            },
          ]}
          emptyState={{ title: "No companies found", description: "Try adjusting your search or filters." }}
        />
      </div>
    </div>
  );
}
